import {
  DropDownDiv,
  Field,
  fieldRegistry,
  type FieldConfig,
  type FieldValidator,
} from 'blockly/core';

import {
  BEGINNER_TIME_UNIT_OPTIONS,
  DEFAULT_BEGINNER_TIME,
  formatBeginnerTimeLabel,
  normalizeBeginnerTimeValue,
  type BeginnerTimeUnit,
  type BeginnerTimeValue,
} from '../constants.js';

export interface FieldBeginnerTimeConfig extends FieldConfig {
  min?: number;
  max?: number;
  precision?: number;
  value?: number;
  unit?: BeginnerTimeUnit;
}

export interface FieldBeginnerTimeFromJsonConfig extends FieldBeginnerTimeConfig {
  name?: string;
}

export class FieldBeginnerTime extends Field<BeginnerTimeValue> {
  override EDITABLE = true;
  override SERIALIZABLE = true;

  protected min_ = 0;
  protected max_ = Number.POSITIVE_INFINITY;
  protected precision_ = 0.1;
  private activeEditor_: HTMLDivElement | null = null;

  constructor(
    value?: BeginnerTimeValue | typeof Field.SKIP_SETUP,
    validator?: FieldValidator<BeginnerTimeValue> | null,
    config?: FieldBeginnerTimeConfig,
  ) {
    super(Field.SKIP_SETUP, validator, config);
    if (value !== Field.SKIP_SETUP) {
      this.setValue(normalizeBeginnerTimeValue(value));
      return;
    }
    this.setValue(
      normalizeBeginnerTimeValue({
        value: config?.value,
        unit: config?.unit,
      }),
    );
  }

  protected override configure_(config: FieldBeginnerTimeConfig): void {
    super.configure_(config);
    if (config.min !== undefined) {
      this.min_ = config.min;
    }
    if (config.max !== undefined) {
      this.max_ = config.max;
    }
    if (config.precision !== undefined) {
      this.precision_ = config.precision;
    }
  }

  protected override initView(): void {
    this.createBorderRect_();
    this.createTextElement_();
    this.render_();
  }

  protected override render_(): void {
    if (!this.textElement_ || !this.textContent_) {
      return;
    }
    this.textContent_.nodeValue = this.getText_() ?? '';
    this.updateSize_();
  }

  protected override getText_(): string {
    const value = this.getValue();
    return value ? formatBeginnerTimeLabel(value) : '';
  }

  protected override doClassValidation_(newValue?: unknown): BeginnerTimeValue | null {
    if (!newValue || typeof newValue !== 'object') {
      return null;
    }
    const candidate = newValue as Partial<BeginnerTimeValue>;
    const unit = candidate.unit === 'MILLISECONDS' ? 'MILLISECONDS' : 'SECONDS';
    let numericValue = Number(candidate.value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }
    if (this.precision_ > 0) {
      numericValue = Math.round(numericValue / this.precision_) * this.precision_;
    }
    numericValue = Math.min(this.max_, Math.max(this.min_, numericValue));
    return { value: numericValue, unit };
  }

  protected override doValueUpdate_(newValue: BeginnerTimeValue): void {
    super.doValueUpdate_(newValue);
    this.render_();
  }

  override saveState(): BeginnerTimeValue {
    return this.getValue() ?? DEFAULT_BEGINNER_TIME;
  }

  override loadState(state: BeginnerTimeValue): void {
    this.setValue(normalizeBeginnerTimeValue(state));
  }

  getMilliseconds(): number {
    const value = this.getValue() ?? DEFAULT_BEGINNER_TIME;
    return value.unit === 'MILLISECONDS'
      ? Math.max(0, Math.round(value.value))
      : Math.max(0, Math.round(value.value * 1000));
  }

  protected override showEditor_(): void {
    const editor = this.createEditor_();
    this.activeEditor_ = editor;
    DropDownDiv.clearContent();
    DropDownDiv.getContentDiv().appendChild(editor);
    DropDownDiv.showPositionedByField(this as unknown as Field, () => {
      this.flushEditorValue_();
      this.activeEditor_ = null;
    });

    const valueInput = editor.querySelector('input');
    valueInput?.focus();
    valueInput?.select();
  }

  private flushEditorValue_(): void {
    const editor = this.activeEditor_;
    if (!editor) {
      return;
    }
    const valueInput = editor.querySelector('input');
    const unitSelect = editor.querySelector('select');
    if (!valueInput || !unitSelect) {
      return;
    }
    const validated = this.doClassValidation_({
      value: Number(valueInput.value),
      unit: unitSelect.value as BeginnerTimeUnit,
    });
    if (validated) {
      this.setValue(validated);
    }
  }

  private createEditor_(): HTMLDivElement {
    const current = this.getValue() ?? DEFAULT_BEGINNER_TIME;
    const editor = document.createElement('div');
    editor.className = 'skyrover-beginner-time-field-editor';

    const valueInput = document.createElement('input');
    valueInput.type = 'number';
    valueInput.min = String(this.min_);
    if (Number.isFinite(this.max_)) {
      valueInput.max = String(this.max_);
    }
    valueInput.step = String(this.precision_);
    valueInput.value = String(current.value);
    valueInput.setAttribute('aria-label', 'Time value');

    const unitSelect = document.createElement('select');
    unitSelect.setAttribute('aria-label', 'Time unit');
    for (const [label, unit] of BEGINNER_TIME_UNIT_OPTIONS) {
      const option = document.createElement('option');
      option.value = unit;
      option.textContent = label;
      option.selected = unit === current.unit;
      unitSelect.appendChild(option);
    }

    const applyValue = () => {
      const validated = this.doClassValidation_({
        value: Number(valueInput.value),
        unit: unitSelect.value as BeginnerTimeUnit,
      });
      if (validated) {
        this.setValue(validated);
      }
    };

    valueInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        applyValue();
        DropDownDiv.hideIfOwner(this as unknown as Field);
      } else if (event.key === 'Escape') {
        DropDownDiv.hideIfOwner(this as unknown as Field);
      }
    });
    // Apply on change only — do not close on blur/unit open, or Milliseconds cannot be chosen.
    valueInput.addEventListener('change', applyValue);
    unitSelect.addEventListener('change', applyValue);

    editor.appendChild(valueInput);
    editor.appendChild(unitSelect);
    return editor;
  }

  override getAriaTypeName(): string | null {
    return 'Time input';
  }

  override getAriaValue(): string | null {
    return this.getText_();
  }

  static override fromJson(options: FieldBeginnerTimeFromJsonConfig): FieldBeginnerTime {
    return new FieldBeginnerTime(
      normalizeBeginnerTimeValue({
        value: options.value,
        unit: options.unit,
      }),
      undefined,
      options,
    );
  }
}

fieldRegistry.register('field_beginner_time', FieldBeginnerTime);
